import { useContext } from 'react'

import { useLatestEntity } from '@domain-group/react-hooks'

import {
  digObject, launchModal, sortArrayBy,
} from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'

import useCurrentUser from '@hooks/useCurrentUser'
import useSelector from '@hooks/useSelector'

import * as imageActions from '@redux/modules/image'
import * as imageLibraryActions from '@redux/modules/imageLibrary'

import PageContext from '@contexts/pageContext'

import getImageResolution from '@functions/getImageResolution'
import { mergeAndStringifyEntityObjects } from '@functions/entity'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type { ImageModel, ImageRequestOptions } from '@models/types'

import * as ModelTypes from '@models/types'

export const generateUrls = (image: Partial<ImageModel> = {}) => {
  const { id, imageable_id, imageable_type } = image

  return {
    campaignImageUrl: imageable_type === 'Campaign' ? `#/campaigns/${imageable_id}/edit/media?image_id=${id}` : '',
  }
}

type UpdateImageParams = {
  dispatch: AppDispatch,
  image: ImageModel,
  imageParams: Partial<ImageModel>,
  requestOptions?: ImageRequestOptions,
}

const updateImage = (params: UpdateImageParams) => {
  const {
    dispatch, image, imageParams, requestOptions,
  } = params
  const { updateImage: updateFn } = imageActions

  const stringifyObjectKeys: (keyof ImageModel)[] = ['data']

  const updatedParams = mergeAndStringifyEntityObjects(image, imageParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteImageParams = {
  dispatch: AppDispatch,
  image: DeleteParams<ImageModel>,
}

const deleteImage = (params: DeleteImageParams) => {
  const { dispatch, image } = params
  const { deleteImage: deleteFn } = imageActions

  return dispatch(deleteFn(image))
}

type UpdateSortOrderParams = {
  dispatch: AppDispatch,
  imageIds: number[],
  requestOptions?: ImageRequestOptions,
}

const updateSortOrder = (params: UpdateSortOrderParams) => {
  const { dispatch, imageIds, requestOptions } = params

  const { updateSortOrder: updateSortFn } = imageActions

  return dispatch(updateSortFn(imageIds, requestOptions))
}

type SetCoverImageParams = {
  dispatch: AppDispatch,
  images: { [key: number | string]: ModelTypes.ImageModel },
  requestOptions?: ImageRequestOptions,
  selectedImage: DeleteParams<ImageModel>,
}
const setCoverImage = (params: SetCoverImageParams) => {
  const {
    dispatch, images, requestOptions, selectedImage,
  } = params

  const { updateSortOrder: updateSortFn } = imageActions
  const { updateImageLibrary } = imageLibraryActions

  const {
    imageable_id,
    imageable_type,
  } = selectedImage

  const entityImages = Object.values(images).filter(image => (
    image.imageable_id === imageable_id && image.imageable_type === imageable_type
  ))
  const sortedEntityImages = sortArrayBy(entityImages, 'asc', 'sort')

  const imageIds = sortedEntityImages.map(image => image.id)

  // Remove selectedImage.id from imageIds, then add to front
  let updatedImageIds = imageIds.filter(id => id !== selectedImage.id)
  updatedImageIds = [selectedImage.id, ...updatedImageIds]

  const updateFn = imageable_type === 'ImageLibrary' ? updateImageLibrary : updateSortFn

  const updatedParams = imageable_type === 'ImageLibrary'
    ? {
      id: selectedImage.imageable_id,
      cover_image_id: selectedImage.id,
    }
    : updatedImageIds

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams, requestOptions))
}

type BulkUpdateImageParams = {
  dispatch: AppDispatch,
  selectedEntityIds: number[],
  updatedParams: Partial<ImageModel>,
}

const bulkUpdateImages = (params: BulkUpdateImageParams) => {
  const {
    dispatch, selectedEntityIds, updatedParams,
  } = params

  const { bulkUpdate: bulkUpdateFn } = imageActions

  // @ts-expect-error TS2345
  return dispatch(bulkUpdateFn(selectedEntityIds, updatedParams))
}

type BulkDeleteImagesParams = {
  dispatch: AppDispatch,
  selectedEntityIds: number[],
}

const bulkDeleteImages = (params: BulkDeleteImagesParams) => {
  const { dispatch, selectedEntityIds } = params
  const { bulkDelete: bulkDeleteFn } = imageActions

  return dispatch(bulkDeleteFn(selectedEntityIds))
}

const apiExportImages = (params: { dispatch: AppDispatch, selectedEntityIds: number[] }) => {
  const { dispatch, selectedEntityIds } = params

  const { apiExportImages: exportFn } = imageActions

  return dispatch(exportFn(selectedEntityIds))
}

function useImage(initEntity: Partial<ImageModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: image }: { entity: ImageModel } = useLatestEntity(initEntity, 'images')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, updating,
  } = useSelector(reduxState => reduxState.images)

  const images = useSelector(reduxState => reduxState.entities.images)

  const resolution = getImageResolution(image)

  const { provider, isProviderUser } = useCurrentUser()
  const providerId = isProviderUser ? provider?.id : null

  const createdDate = digObject(image, 'dates.created.date_time')
  const imageResolution = getImageResolution(image)

  const { image_type } = image

  return {
    callbacks: {
      apiExportImages: (selectedEntityIds: number[]) => apiExportImages({ dispatch, selectedEntityIds }),
      bulkDeleteImages: (selectedEntityIds: number[]) => bulkDeleteImages({ dispatch, selectedEntityIds }),
      bulkUpdateImages: (
        selectedEntityIds: number[],
        updatedParams: Partial<ImageModel>,
      ) => bulkUpdateImages({ dispatch, selectedEntityIds, updatedParams }),
      createOrEditImage: (customPayload?: { callbacks: { afterDeleteAction: () => void } }) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditImageModal',
        payload: { image: { ...image, provider_id: providerId }, ...customPayload },
      }),
      deleteImage: () => deleteImage({ dispatch, image }),
      setCoverImage: (_: unknown, entityOptions?: ImageRequestOptions) => setCoverImage({
        selectedImage: image, images, dispatch, requestOptions: entityOptions,
      }),
      updateImage: (imageParams: Partial<ImageModel>, entityOptions?: ImageRequestOptions) => (
        updateImage({
          image, imageParams, dispatch, requestOptions: entityOptions,
        })
      ),
      updateSortOrder: (imageIds: number[], entityOptions?: ImageRequestOptions) => (
        updateSortOrder({ dispatch, imageIds, requestOptions: entityOptions })
      ),
    },
    creating,
    createdDate,
    deleting,
    image,
    imageResolution,
    isPlan: image_type === 'plan',
    resolution,
    updating,
    urls: generateUrls(image),
  }
}

export type UseImagePayload = ReturnType<typeof useImage>

export default useImage
